// IndexedDB-backed asset cache. Blobs are keyed by sha256 (not signed URL,
// not objectKey) so the cache survives URL rotation, manifest updates, and
// playlist swaps that happen to reuse the same media.

const DB_NAME = 'koolyum-player';
const DB_VERSION = 1;
const STORE_ASSETS = 'assets';
const STORE_META = 'meta';

let dbPromise: Promise<IDBDatabase> | null = null;

function isAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      if (event.oldVersion < 1) {
        db.createObjectStore(STORE_ASSETS); // out-of-line keys (sha256)
        db.createObjectStore(STORE_META);
      }
    };
  });
  return dbPromise;
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// --- Asset blob operations (keyed by sha256) ---

export async function putBlob(sha256: string, blob: Blob): Promise<void> {
  if (!isAvailable()) return;
  const db = await openDb();
  const tx = db.transaction(STORE_ASSETS, 'readwrite');
  await reqToPromise(tx.objectStore(STORE_ASSETS).put(blob, sha256));
}

export async function getBlob(sha256: string): Promise<Blob | null> {
  if (!isAvailable()) return null;
  const db = await openDb();
  const tx = db.transaction(STORE_ASSETS, 'readonly');
  const result = await reqToPromise<Blob | undefined>(
    tx.objectStore(STORE_ASSETS).get(sha256),
  );
  return result ?? null;
}

export async function hasBlob(sha256: string): Promise<boolean> {
  if (!isAvailable()) return false;
  const db = await openDb();
  const tx = db.transaction(STORE_ASSETS, 'readonly');
  const key = await reqToPromise<IDBValidKey | undefined>(
    tx.objectStore(STORE_ASSETS).getKey(sha256),
  );
  return key !== undefined;
}

export async function deleteBlob(sha256: string): Promise<void> {
  if (!isAvailable()) return;
  const db = await openDb();
  const tx = db.transaction(STORE_ASSETS, 'readwrite');
  await reqToPromise(tx.objectStore(STORE_ASSETS).delete(sha256));
}

export async function listCachedHashes(): Promise<string[]> {
  if (!isAvailable()) return [];
  const db = await openDb();
  const tx = db.transaction(STORE_ASSETS, 'readonly');
  const keys = await reqToPromise<IDBValidKey[]>(
    tx.objectStore(STORE_ASSETS).getAllKeys(),
  );
  return keys.filter((k): k is string => typeof k === 'string');
}

export interface CacheStats {
  count: number;
  totalBytes: number;
}

export async function getCacheStats(): Promise<CacheStats> {
  if (!isAvailable()) return { count: 0, totalBytes: 0 };
  const db = await openDb();
  const tx = db.transaction(STORE_ASSETS, 'readonly');
  const blobs = await reqToPromise<Blob[]>(
    tx.objectStore(STORE_ASSETS).getAll(),
  );
  return {
    count: blobs.length,
    totalBytes: blobs.reduce((sum, b) => sum + b.size, 0),
  };
}

// --- Meta (last-good manifest, sync state) ---

export async function setMeta(key: string, value: unknown): Promise<void> {
  if (!isAvailable()) return;
  const db = await openDb();
  const tx = db.transaction(STORE_META, 'readwrite');
  await reqToPromise(tx.objectStore(STORE_META).put(value, key));
}

export async function getMeta<T>(key: string): Promise<T | null> {
  if (!isAvailable()) return null;
  const db = await openDb();
  const tx = db.transaction(STORE_META, 'readonly');
  const result = await reqToPromise<T | undefined>(
    tx.objectStore(STORE_META).get(key),
  );
  return result ?? null;
}

export async function deleteMeta(key: string): Promise<void> {
  if (!isAvailable()) return;
  const db = await openDb();
  const tx = db.transaction(STORE_META, 'readwrite');
  await reqToPromise(tx.objectStore(STORE_META).delete(key));
}

// --- Storage quota check ---

export interface StorageQuota {
  usage: number | null;
  quota: number | null;
  available: number | null;
}

export async function getStorageQuota(): Promise<StorageQuota> {
  if (!navigator.storage?.estimate) {
    return { usage: null, quota: null, available: null };
  }
  try {
    const est = await navigator.storage.estimate();
    const usage = est.usage ?? null;
    const quota = est.quota ?? null;
    const available =
      usage !== null && quota !== null ? quota - usage : null;
    return { usage, quota, available };
  } catch {
    return { usage: null, quota: null, available: null };
  }
}
